import { useTheme } from '@/theme/ThemeProvider'
import { ACCENTS, DISPLAY_FONTS, THEME_NAMES, themeSwatch } from '@/theme/theme'
import './Appearance.css'

/**
 * The Appearance control from the design's left rail.
 *
 * The design puts Theme, Accent and Display font in the rail rather than behind
 * a settings page on purpose: they are stakeholder tweaks, meant to be flipped
 * through in a room. Keeping them one click away is the design, not an
 * oversight to be tidied later.
 */
export function Appearance() {
  const { theme, accent, font, setTheme, setAccent, setFont } = useTheme()

  return (
    <section className="appearance" aria-labelledby="appearance-heading">
      <header className="appearance__head">
        <h2 id="appearance-heading" className="appearance__label">
          Appearance
        </h2>
        <span className="appearance__value">{theme}</span>
      </header>

      <div className="appearance__row" role="radiogroup" aria-label="Theme">
        {THEME_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={name === theme}
            aria-label={name}
            title={name}
            className="appearance__swatch"
            style={{
              background: themeSwatch(name),
              boxShadow: `inset 0 0 0 ${name === theme ? '2px' : '1px'} ${
                name === theme ? 'var(--t-accent)' : 'var(--t-line)'
              }`,
            }}
            onClick={() => setTheme(name)}
          />
        ))}
      </div>

      <div className="appearance__row" role="radiogroup" aria-label="Accent colour">
        {ACCENTS.map((hex) => (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={hex === accent}
            aria-label={`Accent ${hex}`}
            title={hex}
            className="appearance__dot"
            style={{
              background: hex,
              boxShadow: `0 0 0 2px ${hex === accent ? 'var(--t-text)' : 'transparent'}`,
            }}
            onClick={() => setAccent(hex)}
          />
        ))}
      </div>

      <div className="appearance__grid" role="radiogroup" aria-label="Display font">
        {DISPLAY_FONTS.map((name) => (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={name === font}
            className="appearance__font"
            style={{
              background: name === font ? 'var(--t-accent)' : 'var(--t-surface)',
              color: name === font ? 'var(--t-on-accent)' : 'var(--t-text)',
            }}
            onClick={() => setFont(name)}
          >
            {name}
          </button>
        ))}
      </div>
    </section>
  )
}
